import React, { useEffect, useState, useMemo } from "react";
import { useFormik, FormikProvider } from "formik";
import { Badge, Form, Button, Row, Col, InputGroup, ProgressBar } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ShieldCheck, ArrowRightLeft, Landmark, User, Users,
  TrendingUp, Activity, FileText, BadgeCheck, X, Save,
  ChevronRight, ChevronLeft, CheckCircle2, AlertCircle
} from "lucide-react";

import PurchaseOrdersField from "./PurchaseOrdersField";
import { fetchAccounts } from "../../redux/features/accounts.features";
import { getAllPurchases } from "../../redux/features/purchase.features";
import PaymentMethodsField from "./PaymentMethodsField";
import { createPayment } from "../../redux/features/payments.features";
import PaymentAccountSelect from "./PaymentAccountSelect";
import { formatCurrency } from "../../utility";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiServices from "../../services/apiServices";

const PaymentForm = ({ paymentId, propRef, propAmount, propSupplier, onSuccess, hideHeader }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refParam = propRef || searchParams.get("ref");
  const amountParam = propAmount || searchParams.get("amount");

  const accounts = useSelector((state) => state.accountsReducer.accounts);
  const { purchases } = useSelector((state) => state.purchaseReducer);

  const [currentStep, setCurrentStep] = useState(1);
  const [supplierDetails, setSupplierDetails] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = useMemo(() => Yup.object({
    to_account_id: Yup.string().required("Recipient is mandatory"),
    description: Yup.string().required("Payment description is required"),
    amount: Yup.number()
      .required("Amount required")
      .positive("Must be positive")
      .test('amount-match', 'Amount must match total payable', function (value) {
        return !this.parent.payAbleAmount || value === this.parent.payAbleAmount;
      }),
    payment_methods: Yup.array().of(
      Yup.object({
        method: Yup.string().required(),
        amount: Yup.number().min(1).required(),
        from_account_id: Yup.string().required("Source account is required"),
      })
    ).min(1, "At least one payment method is required")
  }), [accounts]);

  const handleSubmit = async (values, { resetForm }) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const agentName = values.agent_id && supplierDetails
        ? supplierDetails.contacts?.find(c => c._id === values.agent_id)?.name
        : null;

      const payload = {
        ...values,
        from_account_id: values.payment_methods[0]?.from_account_id, // Primary fallback
        references: {
          ...values.references,
          supplier: values.references.supplier || propSupplier || null,
          agent_id: values.agent_id || null,
          agent_name: agentName || null,
          purchase_orders: values.references.purchase_orders.map(o => ({
            purchase_id: o.purchase_id,
            paidAmount: o.paidAmount
          }))
        }
      };
      await dispatch(createPayment(payload)).unwrap();
      toast.success("Transaction Committed Successfully");
      resetForm();
      onSuccess ? onSuccess() : navigate("/ListPayments");
    } catch (err) {
      toast.error(err.message || "Execution Failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      from_account_id: "", // Legacy field for single-source compatibility
      to_account_id: "",
      amount: parseFloat(amountParam) || 0,
      payAbleAmount: parseFloat(amountParam) || 0,
      payment_methods: [{
        method: "Cash",
        amount: parseFloat(amountParam) || 0,
        details: {},
        from_account_id: ""
      }],
      transaction_type: "Supplier Payment",
      agent_id: "",
      description: refParam ? `Official Disbursement for Ref: ${refParam}` : "",
      references: { supplier: propSupplier || "", purchase_orders: [] },
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true
  });

  const { values, setFieldValue, handleSubmit: formSubmit, errors, touched, handleChange, isValid, setTouched } = formik;

  useEffect(() => {
    dispatch(fetchAccounts());
    dispatch(getAllPurchases());
  }, [dispatch]);

  // Sync Logic
  useEffect(() => {
    let targetSupplier = propSupplier;
    let targetAgent = "";

    if (!targetSupplier && refParam && purchases.length > 0) {
      const match = purchases.find(p => p.referenceNumber === refParam || p._id === refParam);
      if (match) {
        targetSupplier = match.supplier?._id || match.supplier;
        targetAgent = match.ref_agent_id || "";
        setFieldValue("description", `Settlement for Ref: ${refParam}`);
      }
    }

    if (targetSupplier && accounts.length > 0) {
      const targetAcc = accounts.find(acc => acc.related_party_id === targetSupplier);
      if (targetAcc) {
        setFieldValue("to_account_id", targetAcc._id);
        setFieldValue("references.supplier", targetSupplier);
        if (targetAgent) setFieldValue("agent_id", targetAgent);
        fetchSupplierContext(targetSupplier);
      }
    }
  }, [propSupplier, refParam, purchases, accounts]);

  useEffect(() => {
    const sId = values.references?.supplier;
    if (sId && sId !== supplierDetails?._id) {
      setFieldValue("agent_id", "");
      fetchSupplierContext(sId);
    } else if (!sId) {
      setSupplierDetails(null);
      setFieldValue("agent_id", "");
    }
  }, [values.references?.supplier]);

  const fetchSupplierContext = async (id) => {
    setLoadingContext(true);
    try {
      const res = await apiServices.get(`/suppliers/${id}`);
      if (res.data) setSupplierDetails(res.data);
    } catch (err) {
      console.error("Context Error:", err);
    } finally {
      setLoadingContext(false);
    }
  };

  const accountOptions = useMemo(() => accounts.map(a => ({
    value: a._id,
    account_owner_type: a.account_owner_type,
    label: a.account_name,
    balance: a.balance,
    related_party_id: a.related_party_id
  })), [accounts]);

  const targetAccount = accounts.find(a => a._id === values.to_account_id);

  const nextStep = () => {
    // Validation for Step 1
    if (currentStep === 1) {
      if (!values.to_account_id) {
        setTouched({ to_account_id: true });
        return toast.warn("Please select a recipient account");
      }
    }
    // Validation for Step 3
    if (currentStep === 3) {
      const methodTotal = values.payment_methods.reduce((sum, m) => sum + (m.amount || 0), 0);
      if (Math.abs(methodTotal - values.amount) > 0.01) {
        return toast.error(`Split amounts (Rs. ${methodTotal}) do not match total (Rs. ${values.amount})`);
      }
      if (values.payment_methods.some(m => !m.from_account_id)) {
        return toast.warn("Please select source account for all payment methods");
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { n: 1, title: "Context", icon: <User size={18} /> },
    { n: 2, title: "Allocation", icon: <FileText size={18} /> },
    { n: 3, title: "Settlement", icon: <Landmark size={18} /> },
    { n: 4, title: "Commit", icon: <ShieldCheck size={18} /> },
  ];

  return (
    <FormikProvider value={formik}>
      <div className={hideHeader ? "p-0" : "container-fluid py-4"} style={{ background: '#f1f5f9', minHeight: '100vh' }}>
        <style>
          {`
            .treasury-container {
                max-width: 1200px;
                margin: 0 auto;
                background: white;
                border-radius: 24px;
                box-shadow: 0 40px 80px -20px rgba(0,28,58,0.15);
                overflow: hidden;
                border: 1px solid #e2e8f0;
                min-height: 80vh;
                display: flex;
                flex-direction: column;
            }
            .stepper-header {
                background: #001c3a;
                padding: 40px;
                color: white;
            }
            .step-indicator {
                display: flex;
                justify-content: space-between;
                position: relative;
                margin-bottom: 30px;
                z-index: 1;
            }
            .step-indicator::before {
                content: '';
                position: absolute;
                top: 20px;
                left: 0;
                width: 100%;
                height: 2px;
                background: rgba(255,255,255,0.1);
                z-index: -1;
            }
            .step-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 10px;
                flex: 1;
                transition: all 0.3s;
                opacity: 0.5;
            }
            .step-item.active { opacity: 1; transform: scale(1.1); }
            .step-item.completed { opacity: 0.8; }
            .step-circle {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: rgba(255,255,255,0.1);
                border: 2px solid rgba(255,255,255,0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 900;
                font-size: 14px;
            }
            .active .step-circle {
                background: #3b82f6;
                border-color: #3b82f6;
                box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
            }
            .completed .step-circle {
                background: #10b981;
                border-color: #10b981;
            }
            .stepper-content {
                flex: 1;
                padding: 60px;
                background: #fdfdfe;
            }
            .stepper-footer {
                padding: 30px 60px;
                background: #f8fafc;
                border-top: 1px solid #e2e8f0;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            .executive-label {
                font-size: 11px;
                font-weight: 900;
                color: #001c3a;
                text-transform: uppercase;
                letter-spacing: 1.2px;
                margin-bottom: 12px;
                display: block;
            }
            .summary-card {
                background: #f1f5f9;
                border-radius: 20px;
                padding: 30px;
                border: 1px solid #e2e8f0;
            }
            .method-row-premium {
                background: white;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                padding: 20px;
                margin-bottom: 15px;
                transition: all 0.2s;
            }
            .method-row-premium:hover { border-color: #3b82f6; box-shadow: 0 8px 24px -10px rgba(0,0,0,0.1); }
          `}
        </style>

        <div className="treasury-container animate__animated animate__fadeIn">
          <div className="stepper-header">
            <div className="step-indicator">
              {steps.map(s => (
                <div key={s.n} className={`step-item ${currentStep === s.n ? 'active' : ''} ${currentStep > s.n ? 'completed' : ''}`}>
                  <div className="step-circle">
                    {currentStep > s.n ? <CheckCircle2 size={20} /> : s.icon}
                  </div>
                  <div className="x-small fw-900 uppercase letter-spacing-1">{s.title}</div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <h4 className="fw-900 mb-0">Disbursement Voucher: {values.payment_id || "V-NEW"}</h4>
              <div className="x-small opacity-50 fw-bold mt-1">STEP {currentStep} OF 4 • {steps[currentStep - 1].title.toUpperCase()}</div>
            </div>
          </div>

          <div className="stepper-content">
            <Form onSubmit={formSubmit}>
              {/* STEP 1: CONTEXT & RECIPIENT */}
              {currentStep === 1 && (
                <div className="animate__animated animate__fadeIn">
                  <h5 className="fw-900 text-dark mb-5 border-bottom pb-3">RECIPIENT CHANNELING</h5>
                  <Row className="g-5">
                    <Col lg={12}>
                      <PaymentAccountSelect
                        options={accountOptions}
                        accountOwnerType={values.transaction_type.startsWith("Supplier") ? "Supplier" : values.transaction_type === "Customer Receipt" ? "Customer" : "Employee"}
                        fieldName="to_account_id"
                        label="Pay To (Recipient Account)"
                        placeholder="Select who is receiving this payment..."
                        isDisabled={!!propSupplier}
                      />
                    </Col>
                    <Col lg={6}>
                      <label className="executive-label">Payment Category</label>
                      <Form.Select
                        name="transaction_type"
                        value={values.transaction_type}
                        onChange={handleChange}
                        className="form-control-lg border-2 fw-bold"
                        style={{ height: '55px', borderRadius: '12px', background: '#f8fafc' }}
                      >
                        <option value="Supplier Payment">Supplier Disbursement</option>
                        <option value="Customer Receipt">Customer Receipt (Sales Settlement)</option>
                        <option value="Employee Payment">Employee Salary/Advance</option>
                        <option value="Refund">Customer Refund</option>
                      </Form.Select>
                    </Col>
                    <Col lg={6}>
                      <label className="executive-label">Historical Snap-on Agent</label>
                      <Form.Select
                        name="agent_id"
                        value={values.agent_id}
                        onChange={handleChange}
                        className="form-control-lg border-2 fw-bold"
                        style={{ height: '55px', borderRadius: '12px', background: '#f8fafc' }}
                        disabled={!supplierDetails}
                      >
                        <option value="">No Specific Representative</option>
                        {supplierDetails?.contacts?.map((c, i) => (
                          <option key={i} value={c._id}>{c.name} ({c.designation})</option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>

                  <div className="mt-5 p-4 rounded-4 bg-light border d-flex gap-4 align-items-center">
                    <div className="bg-white p-3 rounded-circle shadow-sm">
                      <User size={32} className="text-primary" />
                    </div>
                    <div>
                      <div className="small fw-900 opacity-50 uppercase">Active Recipient Context</div>
                      <div className="fs-5 fw-bold text-dark">{targetAccount?.account_name || "Waiting for selection..."}</div>
                      <div className="x-small fw-bold text-muted">Balance: {targetAccount ? formatCurrency(targetAccount.balance) : "N/A"}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: ALLOCATION */}
              {currentStep === 2 && (
                <div className="animate__animated animate__fadeIn">
                  <h5 className="fw-900 text-dark mb-5 border-bottom pb-3">ALLOCATION & BILLS</h5>
                  <Row className="g-5">
                    <Col lg={12}>
                      <div className="p-4 bg-light rounded-4 border border-dashed mb-5">
                        <PurchaseOrdersField agent_id={values.agent_id} />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <label className="executive-label">Transaction Narrative (Reference)</label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        className="form-control-lg border-2 fw-bold"
                        style={{ borderRadius: '12px', background: '#f8fafc' }}
                        placeholder="Describe what this payment is for..."
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {/* STEP 3: SETTLEMENT (SPLIT) */}
              {currentStep === 3 && (
                <div className="animate__animated animate__fadeIn">
                  <h5 className="fw-900 text-dark mb-5 border-bottom pb-3">SETTLEMENT CHANNELS</h5>
                  <div className="summary-card mb-5 text-center">
                    <div className="x-small fw-900 opacity-50 uppercase mb-2">Net Payable Amount</div>
                    <h1 className="fw-900 text-primary display-4 mb-3">{formatCurrency(values.amount)}</h1>
                    <Form.Control
                      type="number"
                      className="form-control-lg text-center fw-900 shadow-sm mx-auto"
                      style={{ maxWidth: '300px', borderRadius: '12px', border: '3px solid #001c3a' }}
                      value={values.amount}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        setFieldValue("amount", v);
                        if (values.payment_methods.length === 1) {
                          setFieldValue("payment_methods[0].amount", v);
                        }
                      }}
                    />
                  </div>

                  <PaymentMethodsField />
                </div>
              )}

              {/* STEP 4: COMMIT */}
              {currentStep === 4 && (
                <div className="animate__animated animate__fadeIn">
                  <h5 className="fw-900 text-dark mb-5 border-bottom pb-3">FINAL COMMITMENT</h5>
                  <div className="row g-4">
                    <Col lg={7}>
                      <div className="summary-card h-100">
                        <div className="section-header-v4 mb-4">
                          <Activity size={18} className="text-primary" />
                          <h6 className="mb-0 fw-900 uppercase small">Posting Preview</h6>
                        </div>
                        <div className="d-flex flex-column gap-3">
                          <div className="d-flex justify-content-between">
                            <span className="small text-muted fw-bold">Recipient:</span>
                            <span className="small fw-900 text-dark">{targetAccount?.account_name}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="small text-muted fw-bold">Classification:</span>
                            <span className="small fw-900 text-dark">{values.transaction_type}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="small text-muted fw-bold">Narrative:</span>
                            <span className="small fw-bold text-dark text-end" style={{ maxWidth: '200px' }}>{values.description}</span>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-900 text-dark">TOTAL DISBURSEMENT:</span>
                            <span className="fs-4 fw-900 text-success">{formatCurrency(values.amount)}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col lg={5}>
                      <div className="bg-dark text-white p-5 rounded-4 h-100 d-flex flex-column justify-content-center text-center shadow-lg">
                        <ShieldCheck size={64} className="text-success mx-auto mb-4" />
                        <h3 className="fw-900">Push to Ledger</h3>
                        <p className="opacity-50 small fw-bold">This operation is immutable and will sync across all treasury accounts immediately.</p>
                        <button
                          type="button"
                          className="btn btn-success btn-lg py-3 rounded-pill fw-900 mt-4 shadow-sm"
                          onClick={formSubmit}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "SYNCING..." : "COMMIT TRANSACTION"}
                        </button>
                      </div>
                    </Col>
                  </div>
                </div>
              )}
            </Form>
          </div>

          <div className="stepper-footer">
            <Button
              variant="light"
              className="rounded-pill px-5 py-2 fw-900 text-uppercase border-2"
              onClick={prevStep}
              disabled={currentStep === 1 || isSubmitting}
            >
              <ChevronLeft size={18} className="me-2" /> Back
            </Button>

            {currentStep < 4 ? (
              <Button
                variant="primary"
                className="rounded-pill px-5 py-2 fw-900 text-uppercase shadow-sm"
                onClick={nextStep}
              >
                Continue <ChevronRight size={18} className="ms-2" />
              </Button>
            ) : (
              <div className="d-flex align-items-center gap-2 text-success fw-900 x-small">
                <BadgeCheck size={16} /> DATA INTEGRITY VERIFIED
              </div>
            )}
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};

export default PaymentForm;